const fetchEntity = require("./fetchEntity");
const fetchAncestors = require("./fetchAncestors");
const fs = require("fs").promises;
const { absPath } = require("@parallelpublicworks/gatsby-clf/gatsby-options-index");

// Cache directory
const CACHE_DIR = absPath + "/../clf-cache";

// Create cache key from endpoint
function createCacheKey(endpoint) {
	let slug = endpoint.replace(/\//g, "-").replace(/[^a-zA-Z0-9\-]/g, "_");
	if (slug.endsWith("-")) {
		slug = slug.slice(0, -1);
	}
	return slug;
}

// Ensure cache directory exists
async function ensureCacheDir() {
	try {
		await fs.access(CACHE_DIR);
	} catch {
		await fs.mkdir(CACHE_DIR, { recursive: true });
	}
}

// Load cached data
async function loadCachedData(endpoint) {
	try {
		await ensureCacheDir();
		const cacheKey = createCacheKey(endpoint);
		const cachePath = `${CACHE_DIR}/${cacheKey}.json`;
		const data = await fs.readFile(cachePath, "utf8");
		return JSON.parse(data);
	} catch {
		return null;
	}
}

// Save data to cache
async function saveCachedData(endpoint, data) {
	try {
		if (data && data.attributes) {
			await ensureCacheDir();
			const cacheKey = createCacheKey(endpoint);
			const cachePath = `${CACHE_DIR}/${cacheKey}.json`;
			const data_string = JSON.stringify(data, null, 2);
			await fs.writeFile(cachePath, data_string);
			console.log(`Data cached for endpoint: ${endpoint}`);
		} else {
			throw new Error("Invalid data to cache for endpoint: " + endpoint);
		}
	} catch (error) {
		console.warn("Failed to cache data:", error.message);
	}
}

module.exports = async function fetchPageData({
	endpoint,
	bundles,
	layouts,
	configs,
	headers = null,
	agent = null,
	ancestors = null,
	ssr = false,
	cachedLayouts = null,
	authContext,
	useCache = true,
}) {
	// Check cache first
	if (useCache && ssr) {
		const cachedData = await loadCachedData(endpoint);
		if (cachedData) {
			console.log(`Cache hit for endpoint: ${endpoint}`);
			return cachedData;
		}
	}

	const entity = await fetchEntity({
		endpoint,
		bundles,
		headers,
		agent,
		ssr,
		configs,
		authContext,
	});
	if (entity) {
		let includes = entity.includes ? { ...entity.includes } : {};
		let lists = entity.lists ? { ...entity.lists } : {};
		let relationships = entity.entity.relationships
			? { ...entity.entity.relationships }
			: {};
		ancestors = ancestors || (await fetchAncestors(entity.entity));
		const layout_info = layouts
			? layouts.find((l) => l.types.includes(entity.entity.type))
			: null;
		let layoutEntity = null;
		if (layout_info) {
			if (cachedLayouts && cachedLayouts[layout_info.id]) {
				layoutEntity = cachedLayouts[layout_info.id];
			} else {
				layoutEntity = await fetchEntity({
					endpoint: `node/layout/${layout_info.id}`,
					bundles,
					headers,
					agent,
					ssr,
					configs,
					authContext,
				});
			}
		}

		/**
		 * Merge layout data with the main entity
		 */

		if (layoutEntity) {
			if (layoutEntity.includes) {
				includes = { ...includes, ...layoutEntity.includes };
			}
			if (layoutEntity.lists) {
				lists = { ...lists, ...layoutEntity.lists };
			}
			if (layoutEntity.entity.relationships) {
				const layoutRelNames = [
					"field_section_main",
					"field_section_sidebar",
					"field_section_closing",
				];
				for (const layout_field_name of layoutRelNames) {
					if (
						layoutEntity.entity.relationships[layout_field_name] &&
						Array.isArray(layoutEntity.entity.relationships[layout_field_name].data) &&
						layoutEntity.entity.relationships[layout_field_name].data.length > 0
					) {
						if (
							relationships[layout_field_name] &&
							Array.isArray(relationships[layout_field_name].data) &&
							relationships[layout_field_name].data.length > 0
						) {
							const layout_blocks =
								layoutEntity.entity.relationships[layout_field_name].data;
							const has_blocks_area = layout_blocks.some(
								(block) => block.type === "node--blocks_area"
							);
							if (has_blocks_area) {
								const blocks_area_index = layout_blocks.findIndex(
									(block) => block.type === "node--blocks_area"
								);
								relationships[layout_field_name].data = [
									...layout_blocks.slice(0, blocks_area_index),
									...relationships[layout_field_name].data,
									...layout_blocks.slice(blocks_area_index + 1),
								];
							} else {
								relationships[layout_field_name].data = [
									...relationships[layout_field_name].data,
									...layoutEntity.entity.relationships[layout_field_name].data,
								];
							}
						} else {
							relationships[layout_field_name] =
								layoutEntity.entity.relationships[layout_field_name];
						}
					}
				}
			}
		}

		const restricted = entity.entity.attributes
			? !!entity.entity.attributes.field_restrict
			: false;
		const scheduled = entity.entity.attributes
			? entity.entity.attributes.field_scheduled
			: null;
		const created = entity.entity.attributes ? entity.entity.attributes.created : null;
		const title = entity.entity.attributes ? entity.entity.attributes.title : null;
		const pageData = {
			...entity.entity,
			relationships,
			ancestors,
			includes,
			lists,
			restricted,
			scheduled,
			created,
			title,
			layoutEntity,
			cachedAt: new Date().toISOString(),
		};

		// Save to cache
		if (useCache && ssr) {
			await saveCachedData(endpoint, pageData);
		}

		return pageData;
	}
	return null;
};
